/**
 * @module pages/realtor
 */

import 'jquery';
import 'sticky-kit';

import { sendCriteoEvent } from '@ma-js-common/criteo-helpers';
import Cookies from 'js-cookie';

import { initAccordion } from '../common/accordion';
import { ARROW_DOWN_KEYCODE } from '../common/constants.es6';
import {
    getLeadId,
    initializeRealtorContactForm,
    initializeRealtorMessagePopin,
    initializeStickyContactForm,
    referenceMessage,
} from '../common/contact';
import { isMobile } from '../common/device';
import Form from '../common/form';
import Ga from '../common/ga';
import MAEvent from '../common/ma-event';
import MapboxMap from '../common/map/mapbox-map.es6';
import { patchMapStyle, pricesMapStyle } from '../common/map/styles';
import Navigation from '../common/navigation';
import { initializePastSalesMap } from '../common/realtor-map.es6';
import { initializePlacesTypeahead } from '../common/search';
import { bindForm } from '../common/tagging';
import spinnerTemplate from '../common/templates/spinner-dots.html';
import webglWarningTemplate from '../common/templates/webgl-warning.html';
import { EstateType, getThorViewEventProps, thorAvItems, thorPushEvent } from '../common/thor';
import { addURLParams, getURLParam, hasValue } from '../common/utils';
import Wa from '../common/wa';
import { feedbackDraft } from '../feedback';
import Paginate from '../paginate';

const GA_CATEGORY = 'Page agence';
const WA_SOURCE = 'realtor_page';

/**
 * Handle click on the link to read the full realtor description which is an ellipsis by default
 */
function initializeEllipsis() {
    $('[data-ellipsis-button]').on('click', function onEllipsisClick(event) {
        const ellipsisBlockClass = 'ellipsis-block';
        const button = this;
        const block = button.previousElementSibling;
        const isExpanded = block.getAttribute('aria-expanded') === 'true';

        event.preventDefault();
        button.innerHTML = button.dataset[isExpanded ? 'moreLabel' : 'lessLabel'];

        if (!isExpanded) {
            Ga.send({
                hitType: 'event',
                eventCategory: GA_CATEGORY,
                eventAction: 'realtor:show_more',
                eventLabel: 'realtor:summary:more_link',
            });
        }

        block.setAttribute('aria-expanded', !isExpanded);
        block.classList.toggle(ellipsisBlockClass);
    });
}

/**
 * Handle click on the link to show the realtor phone
 */
function initializePhoneClick() {
    const { realtorId } = MA.Context;
    $('[data-phone-number]').each(function forEachPhoneNumber() {
        const $this = $(this);
        let phoneIsShown = false;

        $this.on('click', e => {
            const eventArgs = {
                av_category: 'find_pro',
                av_items: [
                    {
                        id: realtorId,
                        leadId: getLeadId(realtorId),
                    },
                ],
                av_zone: 'top',
            };
            if (!phoneIsShown) {
                thorPushEvent('lead_agency_phone_display', eventArgs);
            }

            if (!phoneIsShown) {
                e.preventDefault();
                $this
                    .attr('href', `tel:${$this.data('phone-number')}`)
                    .find('[data-phone-placeholder]')
                    .html($this.data('phone-call'));
                phoneIsShown = true;
            }
        });
    });
}

/**
 * Send WebAnalytics page_view tag
 */
function initializeWA() {
    const eventData = {
        realtor_id: MA.Context.realtorId,
    };

    if (Cookies.get('utm_source') === 'criteo') {
        eventData.source = 'retargeting';
    }

    Wa.push('page_view', 'realtor_page', eventData);
    Wa.setTracking();
}

/**
 * Initialize the map for realtor localization
 */
function initializeRealtorLocalizationPopin() {
    $('[data-realtor-address]').on('click', function onRealtorAddressClick(e) {
        e.preventDefault();
        const $this = $(this);
        $.magnificPopup.open(
            {
                type: 'ajax',
                fixedContentPos: 'fixed',
                items: {
                    src: `
                    <div class="popin popin--small">
                        <div class="popin__title-container">
                            <h3 class="popin__title text--h3">${$this.data('title')}</h3>
                        </div>
                        <div class="popin__body no-padding mapboxgl-map vector-map loading-wrapper" data-popin-realtor-map-wrapper>
                            ${webglWarningTemplate()}
                            <div class="vector-map__wrapper">
                                <div class="vector-map__map mapboxgl-map" data-map-container></div>
                            </div>
                            <div class="loading-container">
                                ${spinnerTemplate({ classes: 'spinner--dots' })}
                            </div>
                        </div>
                    </div>
                    `,
                    type: 'inline',
                },

                callbacks: {
                    open() {
                        Navigation.disablePageScroll();

                        const isPublicAddress = $this.data('realtor-is-public-address');
                        const realtorCoordinates = {
                            lng: $this.data('realtor-coordinates-lng'),
                            lat: $this.data('realtor-coordinates-lat'),
                        };

                        const map = new MapboxMap(
                            this.content.find('[data-popin-realtor-map-wrapper]')[0],
                            this.content.find('[data-map-container]')[0],
                            {
                                interactive: false,
                                center: realtorCoordinates,
                                style: patchMapStyle(pricesMapStyle),
                                zoom: 16,
                            },
                        );

                        // Add point for realtor
                        if (isPublicAddress) {
                            map.registerExternalMarker(
                                'loaded-realtor-marker',
                                '/static/images/realtor-marker@2x.png',
                                realtorCoordinates,
                            );
                        }
                    },
                    close() {
                        Navigation.enablePageScroll();
                    },
                },
            },
            0,
        );
    });
}

/**
 * Load and display the popin for the past sale / contact form
 * @param {String} src Url for the content
 * @param {String} gaTagFrom Suffix to send to URL param `ga_tag`
 */
function loadPastSalePopin(src, gaTagFrom) {
    $.magnificPopup.open(
        {
            type: 'ajax',
            fixedContentPos: 'fixed',
            items: {
                src: addURLParams(src, {
                    ga_category: MA.Context.gaCategory,
                    ga_label_prefix: `${MA.Context.gaLabelPrefix}:past_sale_popin`,
                    wa_name_prefix: `${MA.Context.gaLabelPrefix}:past_sale_popin`,
                    wa_source: MA.Context.waSource || MA.Context.gaLabelPrefix,
                    wa_source_deprecated: `${MA.Context.mapGaLabelPrefix}:past_sale`,
                    ga_tag: `from:realtor:map:${gaTagFrom}`,
                    source: 'REALTOR_MESSAGE_SOURCES.REALTOR',
                }),
            },
            callbacks: {
                ajaxContentAdded() {
                    // Ajax content is loaded and appended to DOM
                    Navigation.disablePageScroll();
                    const $form = $(this.content).find('[data-message-form]');

                    $.event.trigger({
                        type: MAEvent.mapReferencePopinOpened,
                        $form,
                    });
                    referenceMessage(
                        this.content,
                        {
                            category: MA.Context.mapGaCategory,
                            actionPrefix: 'past_sale:popin',
                            label: `from:${MA.Context.mapGaLabelPrefix}:${gaTagFrom}`,
                        },
                        {
                            key: 'form_success',
                            name: 'realtor_contact',
                            data: `source=${
                                MA.Context.mapGaLabelPrefix
                            }:past_sales|realtor_id=${$form.data(
                                'realtor-id',
                            )}|reference_id=${$form.data('reference-id')}`,
                        },
                        {
                            key: 'page_view:past_sale',
                            name: `${MA.Context.mapGaLabelPrefix}:past_sales`,
                            data: `source=${MA.Context.waSource ||
                                MA.Context.gaLabelPrefix}|realtor_id=${$form.data(
                                'realtor-id',
                            )}|reference_id=${$form.data('reference-id')}`,
                        },
                    );
                },
                close() {
                    Navigation.enablePageScroll();
                },
            },
        },
        0,
    );
}

/**
 * Open past sale as a popin, not as a new page.
 * @param {Event} event Event
 */
function openPastSalePopin(event) {
    event.preventDefault();
    loadPastSalePopin(
        $(event.currentTarget).attr('href'),
        $(event.currentTarget).data('ga-tag-from'),
    );
}

/**
 * Initialize search form for contextualized past sales map and list
 */
function initializeRelevantPastSalesForm() {
    if (isMobile()) {
        return;
    }

    const gaLabel = 'realtor:map:past_sales:search_bar';

    const $form = $('[data-realtor-relevant-past-sales-form]');

    const $placeField = $form.find('[data-realtor-relevant-past-sales-form-place]');
    const $placeFieldParent = $placeField.parents('.field');

    function togglePlaceFieldErrorClasses() {
        $placeFieldParent.toggleClass('field--invalid');
        $placeFieldParent.find('.field__errors').toggleClass('hide');
    }

    initializePlacesTypeahead($placeField);

    let place = null;

    if (hasValue(MA.Context.place)) {
        const contextPlace = MA.Context.place;
        place = {
            id: contextPlace.id,
            zips: contextPlace.city.zips,
            city_name: contextPlace.city.name,
            name: contextPlace.name_full,
            geotype_label: contextPlace.geotype_label,
        };
    }

    $placeField.on('textchange', () => {
        place = null;

        if ($placeField.parents('.field').hasClass('field--invalid')) {
            togglePlaceFieldErrorClasses();
        }
    });

    $placeField.on(MAEvent.placeSelected, ({ place: eventPlace }) => {
        if (hasValue(eventPlace)) {
            place = eventPlace;
        }
    });

    // We need to trigger a down arrow keydown to show suggestions because
    // there is no simple way to do it with typeahead.js 0.10.5 (> 0.11 handles focus natively)
    const arrowKeydown = $.Event('keydown');
    arrowKeydown.keyCode = ARROW_DOWN_KEYCODE;
    arrowKeydown.which = ARROW_DOWN_KEYCODE;
    $placeField.on('focus', ({ target }) => {
        const $field = $(target);
        if ($field.val()) {
            $(target).trigger(arrowKeydown);
        }
    });

    const $areaField = $form.find('[data-realtor-relevant-past-sales-form-area]');

    Form.bindAjaxForm($form, null, false);
    bindForm($form, {
        category: GA_CATEGORY,
        actionPrefix: 'find_a_pro:search_form',
        label: gaLabel,
    });

    $form.on('submit', event => {
        event.preventDefault();

        if (hasValue(place)) {
            $form.trigger(MAEvent.formSubmitSuccess);

            Ga.send({
                hitType: 'event',
                eventCategory: GA_CATEGORY,
                eventAction: `go:find_a_pro:${place.geotype_label}`,
                eventLabel: gaLabel,
            });

            const $propertyType = $form.find(
                '[data-realtor-relevant-past-sales-form-property-type]:checked',
            );

            const params = {
                place_id: place.id,
                zip: place.zips[0],
                city_name: place.city_name,
                address_name: place.name,
            };

            if ($areaField.val()) {
                params.area = $areaField.val();
            }

            if ($propertyType.length === 1 && $propertyType.val()) {
                params.item_type = $propertyType.val();
            }

            const actionUrl = $form.data('realtor-relevant-past-sales-form-url');
            document.location.href = `${addURLParams(actionUrl, params)}#biens-vendus`;
        } else {
            togglePlaceFieldErrorClasses();
        }
    });
}

/**
 * Show an interactive map with the realtor and its bounded Past Sales
 * ⚠️ Only on Tablet and Desktop
 */
function initializeRealtorPastSalesMap() {
    if (isMobile()) {
        return;
    }

    const wrapperElement = document.querySelector('[data-vector-map]');
    $(wrapperElement).on('click', '[data-popin-button]', openPastSalePopin);

    // second argument is the click handler for the map markers
    initializePastSalesMap(wrapperElement, loadPastSalePopin);
}

/**
 * Add the possibility to switch between Rent and Sell listings on toggle button click
 */
function initializeListingsSwitch() {
    const checkedClassname = 'radio-square--checked';
    $('[name="switch-listings"]').on('change', function onSwitchChange() {
        const $checkbox = $(this);
        const $label = $checkbox.parents('[data-radio-square]');
        $label.addClass(checkedClassname);
        $label.siblings().removeClass(checkedClassname);
        $(`[data-listings-${$checkbox.val()}]`)
            .removeClass('hide')
            .siblings()
            .addClass('hide');

        Wa.setDisplayTracking($('[data-listing-container]'));
    });
}

/**
 * Show a feedback form in a popin when on add feedback button click
 */
function initializeFeedbackPopin() {
    const $popinPlaceholder = $('[data-popin-placeholder]');

    function openFeedbackPopin(url) {
        if ($popinPlaceholder.length < 1) {
            return;
        }

        $.magnificPopup.open(
            {
                type: 'ajax',
                fixedContentPos: 'fixed',
                prependTo: $popinPlaceholder[0],
                enableEscapeKey: false,
                items: {
                    src: url,
                },
                callbacks: {
                    ajaxContentAdded() {
                        // Ajax content is loaded and appended to DOM
                        Navigation.disablePageScroll();
                        feedbackDraft($(this.content));
                    },
                    close() {
                        Navigation.enablePageScroll();
                    },
                },
            },
            0,
        );
    }

    $('[data-add-feedback]').on('click', e => {
        e.preventDefault();
        openFeedbackPopin(MA.Context.feedbackDraftURL);
    });

    if (getURLParam('feedback_draft_id')) {
        openFeedbackPopin(
            `${MA.Context.feedbackDraftURL}?feedback_draft_id=${getURLParam('feedback_draft_id')}`,
        );
    }
    if (getURLParam('feedback_draft')) {
        openFeedbackPopin(MA.Context.feedbackDraftURL);
    }
}

/**
 * Initialize the Past Sale form popin
 */
function initializeReferenceMessagePopin() {
    function showReferenceMessagePopin(event) {
        event.preventDefault();
        const $this = $(this);
        $.magnificPopup.open(
            {
                type: 'ajax',
                fixedContentPos: 'fixed',
                items: {
                    src: addURLParams($this.data('url'), {
                        ga_category: GA_CATEGORY,
                        ga_label_prefix: 'realtor:past_sale_popin',
                        wa_name_prefix: 'realtor:past_sale_popin',
                        wa_source: 'realtor:past_sales',
                        wa_source_deprecated: 'realtor:past_sales',
                        ga_tag: 'from:realtor:past_sales_list',
                        source: 'REALTOR_MESSAGE_SOURCES.REALTOR',
                    }),
                },
                callbacks: {
                    ajaxContentAdded() {
                        // Ajax content is loaded and appended to DOM
                        Navigation.disablePageScroll();
                        const $form = $(this.content).find('[data-message-form]');

                        referenceMessage(
                            this.content,
                            {
                                category: GA_CATEGORY,
                                actionPrefix: 'past_sale:popin',
                                label: $this.data('ua-event-label'),
                            },
                            {
                                key: 'form_success',
                                name: 'realtor_contact',
                                data: `source=${Wa.updateSourceWhenRetargeting(
                                    'realtor:past_sales',
                                )}|realtor_id=${$form.data('realtor-id')}|reference_id=${$form.data(
                                    'reference-id',
                                )}`,
                            },
                            {
                                key: 'page_view:past_sale',
                                name: 'realtor:past_sales:list',
                                data: `source=realtor:past_sales|realtor_id=${$form.data(
                                    'realtor-id',
                                )}|reference_id=${$form.data('reference-id')}`,
                            },
                        );
                    },
                    close() {
                        Navigation.enablePageScroll();
                    },
                },
            },
            0,
        );
    }

    $('[data-reference-container]').on(
        'click',
        '[data-reference-message]',
        showReferenceMessagePopin,
    );
}

/**
 * Handle pagination previous/next button click on several item lists:
 * Past sales (references), Sell listings, Rent listings, Feedbacks
 */
function initializePagination() {
    Paginate().init('[data-paginate-reference]', '[data-reference-container]');
    Paginate().init('[data-paginate-listings-sell]', '[data-listings-sell]');
    Paginate().init('[data-paginate-listings-rent]', '[data-listings-rent]');
    Paginate().init('[data-paginate-feedback]', '[data-feedback-container]');
    Navigation.handleTabsNav($('[data-tabs-nav]'), undefined, 'nav-tabs__item--active');
}

function initializeCriteo() {
    const atype = window.location.pathname.includes('agence') ? 'agency' : 'representative';
    sendCriteoEvent({
        event: 'viewItem',
        item: window.MA.Context.realtorId,
        atype,
    });
}

const dimensionsRealtorContactForm = {
    'REALTOR_MESSAGE_PROJECTS.SELLER': 'sell',
    'REALTOR_MESSAGE_PROJECTS.SELLER_BUYER': 'sell_and_buy',
    'REALTOR_MESSAGE_PROJECTS.BUYER': 'buy',
    'REALTOR_MESSAGE_PROJECTS.TENANT': 'find_rental',
    'REALTOR_MESSAGE_PROJECTS.LESSOR': 'find_tenant',
    'REALTOR_MESSAGE_PROJECTS.MANAGER': 'manage_property',
    'REALTOR_MESSAGE_PROJECTS.OTHER': 'other',
};

function submitRealtorMessageForm() {
    $('[data-realtor-message-form]')
        .children()
        .on('submit', function submitForm() {
            const selectedValue = $('#project')
                .find(':selected')
                .val();

            Ga.set('dimension33', dimensionsRealtorContactForm[selectedValue]);
            Ga.send({
                hitType: 'event',
                eventCategory: 'Page agence',
                eventAction: 'conversion',
                eventLabel: 'contact_agency',
            });
        });
}

const initThorEventTracking = () => {
    const { listingsRent, listingsSell, references } = MA.Context;
    const outlinks = document.querySelectorAll('[data-realtor-outlink]');
    if (outlinks) {
        outlinks.forEach(outlink =>
            outlink.addEventListener('click', () => {
                thorPushEvent('outlink', {
                    link_url: outlink.href,
                    outbound: true,
                    av_zone: 'top',
                });
            }),
        );
    }

    const addEventTrackingOnList = (selector, eventDataSource, params) => {
        if (!eventDataSource.length) {
            return;
        }
        document.querySelectorAll(selector)?.forEach((item, index) => {
            // eslint-disable-next-line camelcase
            const { id, item_type, city, price, zip, place } = eventDataSource[index];
            const { eventTitle, name, listName, distributionType } = params;
            const estateType = EstateType[item_type.key];
            item.addEventListener('click', () => {
                thorPushEvent(eventTitle, {
                    av_estate_type_global: estateType,
                    av_items: [
                        {
                            id,
                            index,
                            estate_type: estateType,
                            publication_id: 'MA',
                            country: 'france',
                            city: city.name,
                            list_name: listName,
                            name,
                            zip_code: zip || place.zip,
                            ...(distributionType && { distribution_type: distributionType }),
                            ...(price && { price }),
                        },
                    ],
                    ...(distributionType && { av_distribution_type_global: distributionType }),
                });
            });
        });
    };

    addEventTrackingOnList('[data-list-references-item]', references, {
        eventTitle: 'select_item',
        listName: 'sold_products',
        name: 'sold_property',
    });
    addEventTrackingOnList('[data-listings-sell] > [data-listing-item]', listingsSell, {
        eventTitle: 'select_item',
        distributionType: '2',
        listName: 'intermediary_property_to_sell',
        name: 'classified',
    });
    addEventTrackingOnList('[data-listings-rent] > [data-listing-item]', listingsRent, {
        eventTitle: 'select_item',
        distributionType: '1',
        listName: 'intermediary_property_to_rent',
        name: 'classified',
    });
};

const realtorPageview = () => {
    const { listingsSell, realtorId, references } = MA.Context;
    const pageTitle = window.location.pathname.includes('agence-immobiliere')
        ? 'agency_detail'
        : 'representative_detail';
    const referenceItems = thorAvItems({
        listings: references,
        listName: 'sold_products',
        name: 'sold_property',
        transactionType: 'TRANSACTION_TYPE.SELL',
    });
    const listingItems = thorAvItems({
        listings: listingsSell,
        listName: 'intermediary_property_to_sell',
        name: 'classified',
        transactionType: 'TRANSACTION_TYPE.SELL',
    });
    thorPushEvent('page_view', {
        ...getThorViewEventProps('find_pro', pageTitle),
        av_items: [{ id: realtorId }, ...referenceItems, ...listingItems],
    });
};

/**
 * Execute all the required mapping when the realtor page is ready
 */
export function domReady() {
    initializeWA();
    initializeEllipsis();
    initAccordion('.realtor-dropdown__header');
    initializePhoneClick();
    initializeListingsSwitch();
    initializeFeedbackPopin();
    initializeReferenceMessagePopin();
    initializeRealtorLocalizationPopin();
    initializePagination();
    initializeRelevantPastSalesForm();
    initializeRealtorPastSalesMap();
    initializeRealtorContactForm(
        GA_CATEGORY,
        'realtor:message_form',
        WA_SOURCE,
        $('[data-realtor-message-form]'),
    );
    initializeRealtorMessagePopin(GA_CATEGORY, WA_SOURCE);
    initializeStickyContactForm(82); // 58 + 24 : navbar + margin
    initializeCriteo();
    submitRealtorMessageForm();
    initThorEventTracking();
    realtorPageview();
}
